'use client'

import {
  Doc,
  Document,
  DocumentBlank,
  Edit,
  Pdf,
  Ppt,
  Xls,
} from '@carbon/icons-react'
// @TODO: unstyle own DialogTrigger so it is usable in more than one place
import { DialogTrigger } from '@radix-ui/react-dialog'
import { Button } from 'app/_components/Button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogSubmit,
  DialogTitle,
} from 'app/_components/Dialog'
import Link from 'next/link'
import type { ReactNode } from 'react'
import React, { useState } from 'react'
import { type Element, Transforms } from 'slate'
import type { RenderElementProps } from 'slate-react'
import {
  ReactEditor,
  useFocused,
  useReadOnly,
  useSelected,
  useSlate,
} from 'slate-react'
import { twMerge } from 'tailwind-merge'

export const fileChipClassName =
  'bg-gray-10 inline-flex items-center gap-2 rounded-xl py-1 pr-1.5 pl-1 leading-none'

export function getIconByType(fileType: string) {
  switch (fileType) {
    case 'application/pdf':
      return Pdf
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return Xls
    case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
      return Ppt
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return Doc
    default:
      return DocumentBlank
  }
}

export default function File({
  attributes,
  children,
  element,
}: RenderElementProps) {
  if (element.type !== 'file') throw new Error('Unsupported file type')

  const isEditable = !useReadOnly()
  const isSelected = useSelected()
  const isFocused = useFocused()
  const Icon = getIconByType(element.fileType)

  return (
    <span
      className="inline-flex items-center gap-2 align-middle"
      {...attributes}
    >
      <Link
        tabIndex={-1}
        href={element.url}
        target="_blank"
        className={twMerge(
          fileChipClassName,
          'align-top transition-colors hover:bg-primary-hover/20',
          isEditable && isSelected && isFocused && 'ring-text-primary ring-2',
        )}
        style={{ textDecoration: 'none' }}
      >
        <Document className="size-4" />
        <span className="font-normal">
          {element.title}
          {children}
        </span>
        <Icon className="size-4" aria-hidden="true" />
      </Link>
      {isEditable && (
        <FileActions
          element={element}
          trigger={
            <Button
              variant="ghost"
              shape="icon"
              className={twMerge(
                fileChipClassName,
                'grow align-top text-sm font-normal transition-colors hover:bg-primary-hover/20',
              )}
              title="Dateinamen bearbeiten"
              aria-label="Dateinamen bearbeiten"
            >
              <Edit aria-hidden="true" />
            </Button>
          }
        />
      )}
    </span>
  )
}

export function FileActions({
  element,
  trigger,
}: {
  element: Element
  trigger: ReactNode
}) {
  if (element.type !== 'file' && element.type !== 'file-block')
    throw new Error('Unsupported file type')

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const editor = useSlate()

  const handleUpdateFileName = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const title = e.currentTarget.fileName.value
    Transforms.setNodes(
      editor,
      { title },
      { at: ReactEditor.findPath(editor, element) },
    )
    setIsEditModalOpen(false)
  }

  return (
    <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogPortal>
        <DialogOverlay>
          <DialogContent>
            <DialogTitle>Dateiname ändern</DialogTitle>
            <form onSubmit={handleUpdateFileName}>
              <input
                name="fileName"
                type="text"
                defaultValue={element.title}
                className="w-full"
              />
              <DialogClose>Abbrechen</DialogClose>
              <DialogSubmit>Speichern</DialogSubmit>
            </form>
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  )
}
